/* All UI: routing, the games, and the screens.
 *
 * The pure logic (scheduler, stages, cloze, text helpers) lives in ../core and
 * is unit-tested there. This module is the DOM layer on top of it.
 */

import { DAY, KEY, MET, GRADUATE, LEARN, STAGES, GAMES, SEED } from "../core/constants.js";
import { stage, ELIG } from "../core/stages.js";
import { state, setState, save, load, blank, seedCards } from "../core/state.js";
import { grade, queue, playable, when, retention, bandOf, RETENTION_BANDS,
         forecast, reviewBalance, todayCount, dailyCapReached } from "../core/scheduler.js";
import { now, today, esc, jsEsc, shuffle, full, norm, cloze, clozeStatus,
         displayTerm, detectSeparable, parseSeparable, pluralForm } from "../core/text.js";
import { beep, buzz, fx } from "../core/audio.js";
import { speak, stopSpeaking, germanVoiceAvailable } from "../core/tts.js";
import { confirmDialog, promptDialog, alertDialog, choiceDialog } from "./modal.js";
import { folderNames, renameFolder as renameFolderIn, deleteFolder as deleteFolderIn } from "../core/folders.js";
import { enrichWords, applyEnrichment, enrichAvailable, enrichMessage } from "../core/enrich.js";

/* What a word looks like on screen: article plus a visible split for
   separable verbs ("ab\u00b7sagen"). full() stays plain, because it is what
   gets spoken aloud. */
const shown = c => (c.art ? c.art + " " : "") + displayTerm(c);

const el = document.getElementById("app");
const $ = id => document.getElementById(id);

/* ---------- routing ---------- */
let view="home", timers=[], raf=null, keyh=null;
function clearAll(){
  timers.forEach(clearTimeout);timers=[];
  if(raf)cancelAnimationFrame(raf);raf=null;
  if(keyh){window.removeEventListener("keydown",keyh);keyh=null;}
  stopSpeaking();
}
const wait=(ms,f)=>timers.push(setTimeout(f,ms));
function go(v){clearAll();view=v;render();window.scrollTo(0,0);}
const VIEWS={home,words,add,stats,how,settings,practice,health};
function render(){
  clearAll();
  if(!state.onboarded)return onboarding();
  if(GAMES[view])return play(view);
  (VIEWS[view]||home)();
}
window.go=go;window.render=render;

/* ---------- home ---------- */
function recommend(){
  const buckets=[0,0,0,0,0,0];
  const active=state.cards.filter(c=>!c.suspended);
  active.forEach(c=>buckets[stage(c)]++);
  const dueN=active.filter(c=>c.due<=now()&&c.reps).length;
  let s=buckets.findIndex((n,i)=>i<5&&n>0);
  if(s<0)s=4;
  const picks=Object.keys(GAMES).filter(k=>GAMES[k].s===s&&playable(k)>0);
  return{buckets,dueN,s,key:picks[0]||"schnell",alt:picks[1],active:active.length};
}
function home(){
  setSession(null,false);
  const r=recommend(), total=state.cards.length, g=GAMES[r.key];
  const susp=total-r.active;
  const why=[
    `${r.buckets[0]} word${r.buckets[0]===1?"":"s"} you haven't met yet. Seeing both sides together is the gentlest way in.`,
    `${r.buckets[1]} word${r.buckets[1]===1?"":"s"} you've seen but can't reliably pick out. Recognition comes before everything else.`,
    `${r.buckets[2]} noun${r.buckets[2]===1?"":"s"} whose article isn't stuck yet. Gender is easiest to drill on its own.`,
    `${r.buckets[3]} word${r.buckets[3]===1?"":"s"} you know on sight but can't produce yet.`,
    `${r.buckets[4]} word${r.buckets[4]===1?"":"s"} ready to be used in a real sentence.`,
    `Everything's holding. Keep the reviews ticking over.`
  ][r.s];

  const groups=[0,1,2,3,4].map(s=>{
    const keys=Object.keys(GAMES).filter(k=>GAMES[k].s===s);
    return`<div class="grp">
      <div class="ghead"><div class="dot" style="background:var(--s${s})"></div>
        <b>${STAGES[s].n}</b><span style="color:var(--muted);font-size:13px">${STAGES[s].e}</span>
        <small>${r.buckets[s]} word${r.buckets[s]===1?"":"s"}</small></div>
      ${keys.map(k=>{const n=playable(k),G=GAMES[k];return`
        <button class="tile ${n?"":"dim"}" ${n?`onclick="go('${k}')"`:"disabled"}>
          <div><b>${G.t}</b><small>${G.d}</small></div>
          ${k===r.key?'<span class="pill">next</span>':""}
          <span class="cnt">${n}</span></button>`}).join("")}
    </div>`;
  }).join("");

  el.innerHTML=`
  <div class="top"><div class="logo">Vokabelspiel</div>
    <div class="streak">${state.streak||0} day${state.streak===1?"":"s"} ·
      <button onclick="toggleSound()">${state.sound?"sound on":"sound off"}</button></div></div>

  <div class="pipe">${r.buckets.map((n,i)=>
    `<div class="seg" style="flex:${Math.max(n,total*0.02)} 1 0" title="${STAGES[i].n}">${
      n?`<span>${n}</span>`:""}</div>`).join("")}</div>
  <div class="pipelbl"><span>${STAGES[0].n}</span><span>how your ${r.active} active word${r.active===1?"":"s"} are spread${susp?` · ${susp} suspended`:""}</span><span>${STAGES[5].n}</span></div>

  ${dailyCapReached()?`<div class="capnote">You've reached today's ${state.dailyCap}-word limit. More is fine, but stopping here is how the schedule stays sustainable. <button class="linkish" onclick="go('health')">Adjust</button></div>`:""}
  <button class="rec" onclick="go('${r.key}')">
    <div class="k">${r.dueN?`${r.dueN} due · `:""}start here</div>
    <b>${g.t}</b><p>${why}</p></button>
  ${r.alt?`<div class="also"><button onclick="go('${r.alt}')">${GAMES[r.alt].t}
     <small>same stage, different angle</small></button></div>`:`<div style="height:18px"></div>`}

  ${groups}
  <div class="row">
    <button class="minor" onclick="go('words')">${total} words</button>
    <button class="minor" onclick="go('add')">Add or import</button>
    <button class="minor" onclick="go('stats')">Progress</button>
  </div>
  <div class="row">
    <button class="minor" onclick="go('practice')">Custom session</button>
    <button class="minor" onclick="go('health')">Retention</button>
  </div>
  <div class="row">
    <button class="minor" onclick="go('how')">How the scoring works</button>
    <button class="minor" onclick="go('settings')">Settings</button>
  </div>`;
}
window.toggleSound=()=>{state.sound=!state.sound;save();if(state.sound)beep("tick");home();};

/* ---------- game chrome ---------- */
let R={}, sprintEnd=0;
/* Set by the session picker; cleared when a round ends. */
let session={folder:null,practice:false};
window.setSession=(folder,practice)=>{session={folder:folder||null,practice:!!practice};};

function play(key){
  const g=GAMES[key];
  if(g.arcade==="blasen")return playBubbles(key);
  const q=queue(key,session);
  if(!q.length){go("home");return;}
  R={key,q,i:0,att:0,right:0,combo:0,best:0,missed:[],credit:0,ups:0,
     sprint:GAMES[key].sprint||0,hints:0,roundHints:0,
     folder:session.folder,practice:session.practice};
  if(R.sprint)sprintEnd=performance.now()+R.sprint*1000;
  STEP[key]();
}

/* ---------- progressive hints ----------
 * Each hint costs 30% of what the answer would otherwise be worth, capped at
 * 90%, so three hints still leave a trace of credit rather than zero. The
 * penalty also blocks the speed bonus entirely (see scheduler.grade). */
const HINT_COST=0.3;
function hintPenalty(){return Math.min(0.9,HINT_COST*(R.hintsThisQ||0));}
function useHint(){
  R.hintsThisQ=(R.hintsThisQ||0)+1;
  R.hints=(R.hints||0)+1;
  beep("tick");
  paintHintNote();
}
function paintHintNote(){
  const n=$("hintnote");if(!n)return;
  const p=hintPenalty();
  n.textContent=p?`hint used, this answer is worth ${Math.round((1-p)*100)}%`:"";
}
/* Rendered by any game that offers hints. */
function hintBar(buttons){
  return `<div class="hintbar">${buttons.map((b,i)=>
    `<button class="hintbtn" id="hint${i}">${esc(b.label)}</button>`).join("")}
    </div><div class="hintnote" id="hintnote"></div>`;
}
function wireHints(buttons){
  buttons.forEach((b,i)=>{
    const el2=$("hint"+i);if(!el2)return;
    el2.onclick=()=>{el2.disabled=true;useHint();b.run();};
  });
}
function chrome(clock){
  return`<div class="bar">
    ${R.sprint
      ? `<span>${R.right} correct of ${R.att}</span>`
      : `<div class="prog"><span style="width:${R.i/R.q.length*100}%"></span></div><span>${R.i} / ${R.q.length}</span>`}
    ${R.combo>1?`<span class="combo">${R.combo}×</span>`:""}
    <button class="x" onclick="go('home')" aria-label="Back">&times;</button></div>
  ${clock||R.sprint?'<div class="clock" id="clk"><span style="width:100%"></span></div>':'<div class="noclock"></div>'}`;
}
function countdown(ms,onEnd){
  const box=$("clk"),bar=box.firstElementChild,t0=performance.now();
  const tick=()=>{
    const left=1-(performance.now()-t0)/ms;
    if(left<=0){bar.style.width="0%";onEnd();return;}
    bar.style.width=(left*100)+"%";box.classList.toggle("low",left<.3);
    raf=requestAnimationFrame(tick);
  };tick();
}
function paintSprint(){
  const box=$("clk");if(!box)return;
  const bar=box.firstElementChild;
  const tick=()=>{
    const left=(sprintEnd-performance.now())/(R.sprint*1000);
    if(left<=0){bar.style.width="0%";finish();return;}
    bar.style.width=(left*100)+"%";box.classList.toggle("low",left<.25);
    raf=requestAnimationFrame(tick);
  };tick();
}
function answer(card,ok,reveal,delay){
  if(raf&&!R.sprint){cancelAnimationFrame(raf);raf=null;}
  R.att++;
  if(ok){R.right++;R.combo++;R.best=Math.max(R.best,R.combo);}
  else{R.combo=0;if(R.missed.indexOf(card)<0)R.missed.push(card);}
  const res=grade(card,ok,GAMES[R.key].sk,R.key,
    {penalty:hintPenalty(),practice:R.practice});
  R.hintsThisQ=0;
  R.credit+=res.credit;
  if(res.leeched)R.leeched=(R.leeched||0)+1;
  if(res.promoted||res.graduated){R.ups++;beep("up");buzz(true);}else fx(ok);
  if(reveal&&$("h"))$("h").innerHTML=reveal;
  if(res.leeched&&$("h"))$("h").innerHTML+=`<br><span class="revealtag">You've missed this ${card.lapses} times, so it's been set aside. Find it under Your words.</span>`;
  wait(delay||(ok?700:(res.leeched?3000:1900)),()=>{
    R.i++;
    if(R.sprint){
      if(performance.now()>=sprintEnd)return finish();
      R.i%=R.q.length;
    }
    STEP[R.key]();
  });
}
function finish(){
  clearAll();
  const shown=R.sprint?R.att:R.q.length;
  const pct=R.right/Math.max(1,shown);
  if(pct>=.8)beep("win");
  el.innerHTML=`<div class="stage">
    <div class="prompt">
      <div class="big pop">${R.right} / ${shown}</div>
      <div class="sub">${R.credit.toFixed(1)} credit earned${
        R.ups?` · ${R.ups} word${R.ups===1?"":"s"} moved up`:""}${
        R.best>2?` · best streak ${R.best}`:""}</div>
      ${R.missed.length?`<div style="text-align:left;margin-top:26px">
        <p class="lede" style="margin-bottom:6px">Missed this round: no credit for these</p>
        <div class="list">${R.missed.slice(0,8).map(c=>`<div class="item"><span class="a ${c.art||""}">${c.art||""}</span>
          <span>${esc(c.term)}</span><span class="t">${esc(c.trans)}</span></div>`).join("")}</div></div>`:""}
    </div>
    ${R.missed.length>1?`<button class="go" onclick="drill()">Drill the ${R.missed.length} misses</button>`:""}
    <button class="go ${R.missed.length>1?"ghost":""}" onclick="go('${R.key}')">Play again</button>
    <button class="go ghost" onclick="go('home')">Back to start</button></div>`;
}
window.drill=()=>{clearAll();
  R={key:R.key,q:shuffle(R.missed),i:0,att:0,right:0,combo:0,best:0,missed:[],credit:0,ups:0,sprint:0};
  STEP[R.key]();};
function keys(map){
  if(keyh)window.removeEventListener("keydown",keyh);
  keyh=e=>{const f=map[e.key.toLowerCase()];if(f){e.preventDefault();f();}};
  window.addEventListener("keydown",keyh);
}
const distract=(c,n)=>{
  let pool=state.cards.filter(x=>x!==c&&x.pos===c.pos);
  if(pool.length<n)pool=state.cards.filter(x=>x!==c);
  return shuffle(pool).slice(0,n);
};

/* ================= Wortfänger: the arcade game ================= */
function arcadeColors(){
  const cs=getComputedStyle(document.documentElement);
  const g=n=>cs.getPropertyValue(n).trim()||"#888";
  return{surface:g("--surface"),ink:g("--ink"),ok:g("--ok"),no:g("--no"),
    der:g("--der"),die:g("--die"),das:g("--das"),a:[g("--der"),g("--die"),g("--das")]};
}
function paintHearts(){
  const h=$("hearts");if(!h)return;
  h.innerHTML=Array.from({length:3},(_,i)=>
    `<span style="opacity:${i<R.lives?1:.18}">&#10084;</span>`).join(" ");
}
function arcadeBurst(x,y,color,n){
  for(let i=0;i<n;i++){
    const a=Math.random()*6.28,sp=1+Math.random()*3;
    R.parts.push({x,y,vx:Math.cos(a)*sp,vy:Math.sin(a)*sp-1,life:1,color});
  }
}
function refreshCombo(){
  const bar=el.querySelector(".bar");if(!bar)return;
  const cb=bar.querySelector(".combo");
  if(R.combo>1&&!cb)bar.insertAdjacentHTML("beforeend",`<span class="combo">${R.combo}×</span>`);
  else if(cb)R.combo>1?cb.textContent=R.combo+"×":cb.remove();
}

/* ================= Wortblasen: the bubble hunt ================= */
function playBubbles(key){
  let pool=queue(key);
  if(!pool.length){go("home");return;}
  R={key,pool,idx:0,lives:3,score:0,combo:0,best:0,credit:0,ups:0,missed:[],
     bubbles:[],parts:[],target:null,resolved:false,over:false,
     ctx:null,cvs:null,cw:0,ch:260,roundEnd:0,roundDur:6200,lastTs:0};
  bubbleRender();
}
function bubbleRender(){
  el.innerHTML=`<div class="stage arcade">
    <div class="bar">
      <span class="hearts" id="hearts"></span>
      ${R.combo>1?`<span class="combo">${R.combo}×</span>`:""}
      <span style="margin-left:auto">Score <b id="score">${R.score}</b></span>
      <button class="x" onclick="go('home')" aria-label="Back">&times;</button></div>
    <div style="text-align:center;padding:10px 0 6px">
      <div class="sub" style="margin:0">find the word that means</div>
      <div class="word sm pop" id="bprompt" style="margin-top:2px"></div>
    </div>
    <div class="clock" id="bclk" style="margin-bottom:10px"><span style="width:100%"></span></div>
    <canvas id="bcv" style="width:100%;height:260px;display:block"></canvas>
    <div class="legend">
      <span><i style="background:var(--der)"></i>der</span>
      <span><i style="background:var(--die)"></i>die</span>
      <span><i style="background:var(--das)"></i>das</span>
      <span><i style="background:var(--ink)"></i>other</span></div>
    <p class="lede" id="bhint" style="text-align:center;margin:8px 0 0;min-height:1.4em">&nbsp;</p>
  </div>`;
  const cvs=$("bcv");
  const dpr=window.devicePixelRatio||1, cssW=cvs.clientWidth, cssH=260;
  cvs.width=Math.round(cssW*dpr);cvs.height=Math.round(cssH*dpr);
  R.cvs=cvs;R.ctx=cvs.getContext("2d");R.ctx.setTransform(dpr,0,0,dpr,0,0);
  R.cw=cssW;R.ch=cssH;
  paintHearts();
  cvs.addEventListener("pointerdown",e=>{
    const rect=cvs.getBoundingClientRect();
    bubbleHit(e.clientX-rect.left,e.clientY-rect.top);
  });
  bubbleNext();
  R.lastTs=performance.now();
  raf=requestAnimationFrame(bubbleTick);
}
function fitBubbleText(ctx,text,maxWidth,maxFont,minFont){
  let size=maxFont;
  ctx.font=`700 ${size}px Archivo,sans-serif`;
  while(size>minFont&&ctx.measureText(text).width>maxWidth){
    size--;ctx.font=`700 ${size}px Archivo,sans-serif`;
  }
  let t=text;
  while(t.length>1&&ctx.measureText(t+"…").width>maxWidth)t=t.slice(0,-1);
  return{size,text:t===text?text:t+"…"};
}
function bubbleFreeSpot(w,h,placed,cw,ch){
  const fits=(x,y)=>placed.every(p=>!(Math.abs(p.x-x)<(p.w+w)/2+6 && Math.abs(p.y-y)<(p.h+h)/2+6));
  for(let t=0;t<60;t++){
    const x=w/2+Math.random()*(cw-w), y=h/2+Math.random()*(ch-h);
    if(fits(x,y))return{x,y};
  }
  /* random retries failed, fall back to a systematic scan so bubbles are NEVER allowed to overlap */
  for(let y=h/2;y<=ch-h/2;y+=h*0.4){
    for(let x=w/2;x<=cw-w/2;x+=w*0.3){
      if(fits(x,y))return{x,y};
    }
  }
  return null; /* genuinely no room left, caller must shrink or skip */
}
function bubbleNext(){
  if(R.idx>=R.pool.length){R.pool=shuffle(R.pool);R.idx=0;}
  const c=R.pool[R.idx++], C=arcadeColors();
  let capBubbles=R.cw<380?5:R.cw<440?6:7;      /* narrow phones get fewer, roomier bubbles */
  let n=Math.min(capBubbles-1,3+Math.floor(R.combo/3));
  const ctx=R.ctx;
  let h,maxW,all,placed;
  /* if bubbles genuinely can't all fit without overlap, drop one and try again rather than ever overlapping */
  for(;;){
    all=shuffle([c].concat(distract(c,n)));
    h=Math.max(38,54-all.length*1.6);
    maxW=Math.min(R.cw*0.42,170);
    placed=[];
    let ok=true;
    for(const card of all){
      const fit=fitBubbleText(ctx,displayTerm(card),maxW-18,16,10);
      ctx.font=`700 ${fit.size}px Archivo,sans-serif`;
      const w=Math.max(h,ctx.measureText(fit.text).width+22);
      const spot=bubbleFreeSpot(w,h,placed,R.cw,R.ch);
      if(!spot){ok=false;break;}
      const ang=Math.random()*6.283,sp=0.02+Math.random()*0.025;
      const color=card.art==="der"?C.der:card.art==="die"?C.die:card.art==="das"?C.das:C.ink;
      placed.push({card,x:spot.x,y:spot.y,w,h,fitSize:fit.size,fitText:fit.text,
        vx:Math.cos(ang)*sp,vy:Math.sin(ang)*sp,color});
    }
    if(ok||n<=2)break;
    n--; /* one fewer decoy, try the layout again */
  }
  R.bubbles=placed;R.target=c;R.resolved=false;
  $("bprompt").textContent=c.trans;
  $("bhint").innerHTML="&nbsp;";
  R.roundDur=Math.max(3200,6200-R.combo*220);
  R.roundEnd=performance.now()+R.roundDur;
}
function bubbleHit(cx,cy){
  if(R.over||R.resolved||!R.bubbles.length)return;
  /* search from the last-drawn (topmost) backward, so an overlapping tap hits what's visually on top */
  for(let i=R.bubbles.length-1;i>=0;i--){
    const b=R.bubbles[i];
    if(Math.abs(b.x-cx)<=b.w/2&&Math.abs(b.y-cy)<=b.h/2){bubbleResolve(b);return;}
  }
}
function bubbleResolve(hit){
  R.resolved=true;
  const ok=hit.card===R.target;
  arcadeBurst(hit.x,hit.y,ok?arcadeColors().ok:arcadeColors().no,ok?18:10);
  if(!ok){
    $("bcv").classList.add("shake");wait(320,()=>$("bcv")&&$("bcv").classList.remove("shake"));
    const t=R.bubbles.find(b=>b.card===R.target);if(t)t.reveal=true;
    $("bhint").innerHTML=`that was <b>${esc(hit.card.trans)}</b>, you needed <b>${esc(R.target.term)}</b> <span class="revealtag">(circled above)</span>`;
  }
  bubbleSettle(R.target,ok);
}
function bubbleTimeout(){
  if(R.resolved||R.over)return;
  R.resolved=true;
  const t=R.bubbles.find(b=>b.card===R.target);if(t)t.reveal=true;
  $("bhint").innerHTML=`too slow, it was <b>${esc(R.target.term)}</b> <span class="revealtag">(circled above)</span>`;
  bubbleSettle(R.target,false);
}
function bubbleSettle(c,ok){
  const res=grade(c,ok,GAMES[R.key].sk,R.key);
  R.credit+=res.credit;
  if(ok){R.score+=10+R.combo*2;R.combo++;R.best=Math.max(R.best,R.combo);}
  else{R.lives--;R.combo=0;if(R.missed.indexOf(c)<0)R.missed.push(c);}
  if(res.promoted||res.graduated){R.ups++;beep("up");}else fx(ok);
  paintHearts();$("score")&&($("score").textContent=R.score);
  refreshCombo();
  if(ok)R.bubbles=[];   /* on a miss, bubbles stay frozen on screen (see bubbleTick) so the reveal is visible */
  if(R.lives<=0){wait(ok?650:3000,bubbleFinish);return;}
  wait(ok?550:3000,bubbleNext);
}
function bubbleTick(ts){
  if(R.over)return;
  const dt=ts-R.lastTs;R.lastTs=ts;
  const ctx=R.ctx;
  ctx.clearRect(0,0,R.cw,R.ch);
  ctx.fillStyle=arcadeColors().surface;ctx.fillRect(0,0,R.cw,R.ch);

  if(!R.resolved){
    const left=Math.max(0,(R.roundEnd-ts)/R.roundDur);
    const clk=$("bclk"),bar=clk&&clk.firstElementChild;
    if(bar){bar.style.width=(Math.min(1,left)*100)+"%";clk.classList.toggle("low",left<0.25);}
    if(ts>=R.roundEnd)bubbleTimeout();
  }

  R.bubbles.forEach(b=>{
    if(!R.resolved){
      b.x+=b.vx*dt;b.y+=b.vy*dt;
      if(b.x-b.w/2<0){b.x=b.w/2;b.vx=-b.vx;} if(b.x+b.w/2>R.cw){b.x=R.cw-b.w/2;b.vx=-b.vx;}
      if(b.y-b.h/2<0){b.y=b.h/2;b.vy=-b.vy;} if(b.y+b.h/2>R.ch){b.y=R.ch-b.h/2;b.vy=-b.vy;}
    }
    const rr=b.h/2;
    if(b.reveal){
      const pulse=2+Math.sin(ts/120)*1.5;
      ctx.save();ctx.beginPath();
      if(ctx.roundRect)ctx.roundRect(b.x-b.w/2-6,b.y-b.h/2-6,b.w+12,b.h+12,rr+6);
      ctx.lineWidth=3+pulse;ctx.strokeStyle=arcadeColors().ok;ctx.stroke();ctx.restore();
    }
    ctx.save();
    ctx.beginPath();
    if(ctx.roundRect)ctx.roundRect(b.x-b.w/2,b.y-b.h/2,b.w,b.h,rr);
    else{ /* manual stadium path for engines without roundRect */
      const x0=b.x-b.w/2,y0=b.y-b.h/2,x1=b.x+b.w/2,y1=b.y+b.h/2;
      ctx.moveTo(x0+rr,y0);ctx.lineTo(x1-rr,y0);ctx.arc(x1-rr,y0+rr,rr,-Math.PI/2,Math.PI/2);
      ctx.lineTo(x0+rr,y1);ctx.arc(x0+rr,y0+rr,rr,Math.PI/2,-Math.PI/2);
    }
    ctx.fillStyle=b.color;ctx.globalAlpha=b.reveal||!R.resolved?0.93:0.4;ctx.fill();ctx.globalAlpha=1;
    ctx.lineWidth=2;ctx.strokeStyle="rgba(255,255,255,.55)";ctx.stroke();
    ctx.fillStyle="#fff";ctx.font=`700 ${b.fitSize}px Archivo,sans-serif`;
    ctx.textAlign="center";ctx.textBaseline="middle";
    ctx.fillText(b.fitText,b.x,b.y+1);
    ctx.restore();
  });

  R.parts=R.parts.filter(p=>p.life>0);
  R.parts.forEach(p=>{
    p.x+=p.vx;p.y+=p.vy;p.vy+=0.12;p.life-=0.035;
    ctx.globalAlpha=Math.max(0,p.life);ctx.fillStyle=p.color;
    ctx.beginPath();ctx.arc(p.x,p.y,3,0,6.283);ctx.fill();ctx.globalAlpha=1;
  });

  raf=requestAnimationFrame(bubbleTick);
}
function bubbleFinish(){
  R.over=true;clearAll();
  if(R.best>=6)beep("win");
  el.innerHTML=`<div class="stage"><div class="prompt">
    <div class="big pop">${R.score}</div>
    <div class="sub">points · best combo ${R.best} · ${R.credit.toFixed(1)} credit earned${
      R.ups?` · ${R.ups} word${R.ups===1?"":"s"} moved up`:""}</div>
    ${R.missed.length?`<div style="text-align:left;margin-top:26px">
      <p class="lede" style="margin-bottom:6px">Got past you</p>
      <div class="list">${R.missed.slice(0,8).map(c=>`<div class="item"><span class="a ${c.art||""}">${c.art||""}</span>
        <span>${esc(c.term)}</span><span class="t">${esc(c.trans)}</span></div>`).join("")}</div></div>`:""}
  </div>
  <button class="go" onclick="go('blasen')">Play again</button>
  <button class="go ghost" onclick="go('home')">Back to start</button></div>`;
}



/* ================= games ================= */
const STEP={};

STEP.artikel=()=>{
  if(R.i>=R.q.length)return finish();
  const c=R.q[R.i];
  const AHINTS=[
    {label:"Rule out one",run:()=>{
      const wrong=["der","die","das"].filter(a=>a!==c.art);
      const drop=shuffle(wrong)[0];
      const b=[...el.querySelectorAll(".art")].find(x=>x.dataset.a===drop);
      if(b){b.disabled=true;b.classList.add("miss");}
    }},
    {label:"Hear it",run:()=>speak(full(c))},
  ];
  el.innerHTML=`<div class="stage">${chrome(false)}
    <div class="prompt"><div class="word pop">${esc(c.term)}</div><div class="hint" id="h">&nbsp;</div></div>
    <div class="arts">${["der","die","das"].map((a,k)=>
      `<button class="art ${a}" data-a="${a}">${a}<kbd>${k+1}</kbd></button>`).join("")}</div>
    ${hintBar(AHINTS)}</div>`;
  wireHints(AHINTS);
  const pick=a=>{
    const ok=a===c.art;
    el.querySelectorAll(".art").forEach(x=>{x.disabled=true;if(x.dataset.a!==c.art)x.classList.add("miss");});
    if(!ok)el.querySelector(".word").classList.add("shake");
    answer(c,ok, ok?`<b>${esc(full(c))}</b>${c.plural?`, die ${esc(c.plural)}`:""}<br>${esc(c.trans)}`
      :`It's <b>${esc(full(c))}</b>, meaning ${esc(c.trans)}`);
  };
  el.querySelectorAll(".art").forEach(b=>b.onclick=()=>pick(b.dataset.a));
  keys({1:()=>pick("der"),2:()=>pick("die"),3:()=>pick("das")});
};

STEP.paare=()=>{
  const q=R.q;let sel=null,left=q.length;
  const tiles=shuffle(q.map(c=>({c,side:"de"})).concat(q.map(c=>({c,side:"en"}))));
  el.innerHTML=`<div class="stage">${chrome(false)}
    <div class="grid">${tiles.map((t,k)=>
      `<button class="pair" data-k="${k}">${esc(t.side==="de"?shown(t.c):t.c.trans)}</button>`).join("")}</div>
    <div class="hint" id="h" style="text-align:center">Tap a German word, then its meaning.</div></div>`;
  el.querySelectorAll(".pair").forEach(b=>b.onclick=()=>{
    const t=tiles[b.dataset.k];
    if(sel&&sel.b===b){b.classList.remove("sel");sel=null;return;}
    if(!sel){sel={b,t};b.classList.add("sel");beep("tick");return;}
    const hit=sel.t.c===t.c&&sel.t.side!==t.side;
    const res=grade(t.c,hit,"rec","paare");R.credit+=res.credit;R.att++;
    if(hit){
      b.classList.add("done");sel.b.classList.remove("sel");sel.b.classList.add("done");
      R.right++;R.i++;left--;
      if(res.promoted||res.graduated){R.ups++;beep("up");}else fx(true);
      const p=el.querySelector(".prog span");if(p)p.style.width=(R.i/q.length*100)+"%";
      if(!left)wait(420,finish);
    }else{
      b.classList.add("bad");const s=sel.b;s.classList.add("bad");
      if(R.missed.indexOf(t.c)<0)R.missed.push(t.c);
      fx(false);
      wait(460,()=>{b.classList.remove("bad");s.classList.remove("bad","sel");});
    }
    sel=null;
  });
};

STEP.wahr=()=>{
  const c=R.q[R.i];
  const lie=Math.random()<.5;
  const other=distract(c,1)[0]||c;
  const shown=lie?other.trans:c.trans;
  el.innerHTML=`<div class="stage">${chrome(false)}
    <div class="prompt"><div class="word sm pop">${esc(shown(c))}</div>
      <div style="font-size:23px;font-weight:600;margin-top:14px">${esc(shown)}</div>
      <div class="hint" id="h">&nbsp;</div></div>
    <div class="two">
      <button class="opt" id="y">richtig</button>
      <button class="opt" id="n">falsch</button></div>
    <div class="sub" style="text-align:center">F and J work too</div></div>`;
  paintSprint();
  let done=false;
  const pick=said=>{
    if(done)return;done=true;
    const ok=said===!lie;
    $("y").classList.add(!lie?"right":"wrong");$("n").classList.add(lie?"right":"wrong");
    answer(c,ok, ok?"&nbsp;":`<b>${esc(full(c))}</b> means ${esc(c.trans)}`, ok?520:1500);
  };
  $("y").onclick=()=>pick(true);$("n").onclick=()=>pick(false);
  keys({f:()=>pick(true),j:()=>pick(false),arrowleft:()=>pick(true),arrowright:()=>pick(false)});
};

STEP.schnell=()=>{
  if(R.i>=R.q.length)return finish();
  const c=R.q[R.i];
  const opts=shuffle([c].concat(distract(c,3)));
  const HINTS=[
    {label:"Remove two",run:()=>{
      /* drop two wrong options, leaving a straight 1-in-2 */
      const wrong=opts.map((o,k)=>k).filter(k=>opts[k]!==c);
      shuffle(wrong).slice(0,2).forEach(k=>{
        const b=el.querySelectorAll(".opt")[k];if(b){b.disabled=true;b.style.opacity=".25";}
      });
    }},
    {label:"Hear it",run:()=>speak(full(c))},
  ];
  el.innerHTML=`<div class="stage">${chrome(true)}
    <div class="prompt"><div class="word pop">${esc(shown(c))}</div><div class="hint" id="h">&nbsp;</div></div>
    <div class="opts">${opts.map((o,k)=>
      `<button class="opt" data-k="${k}"><kbd>${k+1}</kbd>${esc(o.trans)}</button>`).join("")}</div>
    ${hintBar(HINTS)}</div>`;
  wireHints(HINTS);
  let done=false;
  const pick=k=>{
    if(done)return;done=true;
    const ok=k!==null&&opts[k]===c;
    el.querySelectorAll(".opt").forEach((x,j)=>{x.disabled=true;if(opts[j]===c)x.classList.add("right");});
    if(k!==null&&!ok)el.querySelectorAll(".opt")[k].classList.add("wrong");
    answer(c,ok, ok?"&nbsp;":esc(c.ex), ok?600:1700);
  };
  el.querySelectorAll(".opt").forEach(b=>b.onclick=()=>pick(+b.dataset.k));
  keys({1:()=>pick(0),2:()=>pick(1),3:()=>pick(2),4:()=>pick(3)});
  countdown(7000,()=>pick(null));
};

STEP.salat=()=>{
  if(R.i>=R.q.length)return finish();
  const c=R.q[R.i];
  const target=c.term.split("");
  let letters=shuffle(target.map(ch=>({ch})));
  if(letters.map(x=>x.ch).join("")===c.term)letters=letters.reverse();
  let built=[];
  const draw=()=>{
    $("slots").innerHTML=target.map((_,k)=>
      `<div class="slot ${built[k]?"on":""}">${built[k]?esc(built[k].ch):""}</div>`).join("");
    el.querySelectorAll(".ltr").forEach((b,k)=>b.classList.toggle("used",built.some(x=>x.i===k)));
  };
  const check=()=>{
    if(built.length<target.length)return;
    const ok=built.map(x=>x.ch).join("")===c.term;
    if(!ok)$("slots").classList.add("shake");
    answer(c,ok, ok?`<b>${esc(full(c))}</b>`:`<b>${esc(full(c))}</b>. ${esc(c.ex)}`);
  };
  const SHINTS=[
    {label:"Place next letter",run:()=>{
      /* fill the next empty slot with the correct letter, consuming a tile */
      const idx=built.length;
      if(idx>=target.length)return;
      const want=target[idx];
      const k=letters.findIndex((x,i)=>x.ch===want&&!built.some(b=>b.i===i));
      if(k>=0){built.push({ch:want,i:k});draw();check();}
    }},
    {label:"Hear it",run:()=>speak(full(c))},
  ];
  el.innerHTML=`<div class="stage">${chrome(false)}
    <div class="prompt"><div class="word sm">${esc(c.trans)}</div>
      <div class="sub">${c.pos==="n"?"noun":c.pos==="v"?"verb":"adjective"} · ${target.length} letters${
        c.art?` <span class="artpill ${c.art}">${c.art}</span>`:""}</div>
      <div class="hint" id="h">&nbsp;</div></div>
    <div class="slots" id="slots"></div>
    <div class="letters">${letters.map((x,k)=>
      `<button class="ltr" data-k="${k}">${esc(x.ch)}</button>`).join("")}</div>
    <button class="go ghost" id="undo">Undo</button>
    ${hintBar(SHINTS)}</div>`;
  wireHints(SHINTS);
  el.querySelectorAll(".ltr").forEach((b,k)=>b.onclick=()=>{
    if(built.length>=target.length)return;
    built.push({ch:letters[k].ch,i:k});beep("tick");draw();check();
  });
  $("undo").onclick=()=>{built.pop();draw();};
  keys({backspace:()=>{built.pop();draw();}});
  draw();
};

STEP.plural=()=>{
  if(R.i>=R.q.length)return finish();
  const c=R.q[R.i];
  const answerPl=pluralForm(c);
  const PHINTS=[
    {label:"First letter",run:()=>{$("h").innerHTML=`starts with <b>${esc(answerPl.slice(0,1))}</b>`;$("in").focus();}},
    {label:"Hear it",run:()=>speak("die "+answerPl)},
  ];
  el.innerHTML=`<div class="stage">${chrome(false)}
    <div class="prompt"><div class="word sm pop">${esc(shown(c))}</div>
      <div class="sub">${esc(c.trans)}</div>
      <div class="sub" style="margin-top:14px;color:var(--ink);font-weight:600">die \u2026?</div>
      <div class="hint" id="h">What is the plural?</div></div>
    <input type="text" id="in" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Plural form">
    <button class="go" id="ok">Check</button>
    ${hintBar(PHINTS)}</div>`;
  wireHints(PHINTS);
  const inp=$("in");inp.focus();
  const check=()=>{
    if(inp.disabled)return;
    const ok=norm(inp.value)===norm(answerPl);
    inp.classList.add(ok?"right":"wrong");inp.disabled=true;
    if(!ok)inp.classList.add("shake");
    answer(c,ok,`<b>die ${esc(answerPl)}</b>${answerPl===c.term?" (unchanged in the plural)":""}`);
  };
  $("ok").onclick=check;
  inp.onkeydown=e=>{if(e.key==="Enter")check();};
};

STEP.tippen=()=>{
  if(R.i>=R.q.length)return finish();
  const c=R.q[R.i];
  const HINTS=[
    {label:"First letter",run:()=>{$("h").innerHTML=`starts with <b>${esc(c.term.slice(0,1))}</b>`;$("in").focus();}},
    {label:"Two more",run:()=>{$("h").innerHTML=`starts with <b>${esc(c.term.slice(0,3))}</b>`;$("in").focus();}},
    {label:"Hear it",run:()=>{speak(full(c));$("h").innerHTML="listen closely";$("in").focus();}},
  ];
  el.innerHTML=`<div class="stage">${chrome(false)}
    <div class="prompt"><div class="word sm pop">${esc(c.trans)}</div>
      <div class="sub">${c.pos==="n"?`noun${c.art?` <span class="artpill ${c.art}">${c.art}</span>`:""} · article optional`
        :c.pos==="v"?"verb":"adjective"}</div>
      <div class="hint" id="h">&nbsp;</div></div>
    <input type="text" id="in" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" placeholder="Type the German">
    <button class="go" id="ok">Check</button>
    ${hintBar(HINTS)}</div>`;
  const inp=$("in");inp.focus();
  wireHints(HINTS);
  const check=()=>{
    if(inp.disabled)return;
    const ok=norm(inp.value)===norm(c.term);
    inp.classList.add(ok?"right":"wrong");inp.disabled=true;
    if(!ok)inp.classList.add("shake");
    answer(c,ok, ok?`<b>${esc(full(c))}</b>`:`<b>${esc(full(c))}</b>. ${esc(c.ex)}`);
  };
  $("ok").onclick=check;
  inp.onkeydown=e=>{if(e.key==="Enter")check();};
};

/* Hiding "den Abfalleimer" together stops the article giving the gender away,
   but two words vanishing for a one-word answer reads as a bug. So prefer to
   leave the article visible and make every option the same gender instead,
   which gives nothing away either. Fall back to hiding both when there aren't
   enough same-gender nouns to fill the options. */
function sameGenderPool(c){
  return state.cards.filter(x=>x!==c&&!x.suspended&&x.pos==="n"&&x.art===c.art);
}
function planCloze(c,cz){
  if(!cz.det)return{ranges:cz.ranges,pool:null};
  const same=sameGenderPool(c);
  return same.length>=3?{ranges:[[cz.i,cz.i]],pool:same}:{ranges:cz.ranges,pool:null};
}
/* Render the sentence with every hidden span blanked out.
   Two details that look trivial and are not:
   - a range covers its internal whitespace too, or the gap ends up with a
     stray double space beside it
   - punctuation attached to a hidden word is kept, so the sentence still
     ends with its full stop. */
function clozeHtml(cz,ranges){
  const hidden=new Set(),starts=new Map();
  ranges.forEach(([a,b])=>{
    starts.set(a,b);
    for(let k=a;k<=b;k++)hidden.add(k);
  });
  let out="";
  for(let k=0;k<cz.toks.length;k++){
    if(starts.has(k)){
      out+='<span class="gap">&nbsp;</span>';
      const tail=String(cz.toks[starts.get(k)]).match(/[^\p{L}]+$/u);
      if(tail)out+=esc(tail[0]);
    }
    if(hidden.has(k))continue;
    out+=esc(cz.toks[k]);
  }
  return out;
}

STEP.luecke=()=>{
  if(R.i>=R.q.length)return finish();
  const c=R.q[R.i],cz=cloze(c);
  if(!cz){R.i++;return STEP.luecke();}
  const plan=planCloze(c,cz);
  const out=clozeHtml(cz,plan.ranges);
  const opts=shuffle([c].concat(plan.pool?shuffle(plan.pool).slice(0,3):distract(c,3)));
  const LHINTS=[
    {label:"Remove two",run:()=>{
      const wrong=opts.map((o,k)=>k).filter(k=>opts[k]!==c);
      shuffle(wrong).slice(0,2).forEach(k=>{
        const b=el.querySelectorAll(".opt")[k];if(b){b.disabled=true;b.style.opacity=".25";}
      });
    }},
  ];
  el.innerHTML=`<div class="stage">${chrome(false)}
    <div class="prompt"><div class="word sm pop">${out}</div>
      <div class="hint" id="h">${cz.split
        ? "This verb splits, so two gaps belong to one word."
        : "Which word belongs in the gap?"}</div></div>
    <div class="opts">${opts.map((o,k)=>
      `<button class="opt" data-k="${k}"><kbd>${k+1}</kbd>${esc(displayTerm(o))}</button>`).join("")}</div>
    ${hintBar(LHINTS)}</div>`;
  wireHints(LHINTS);
  let done=false;
  const pick=k=>{
    if(done)return;done=true;
    const ok=opts[k]===c;
    el.querySelectorAll(".opt").forEach((x,j)=>{x.disabled=true;if(opts[j]===c)x.classList.add("right");});
    if(!ok)el.querySelectorAll(".opt")[k].classList.add("wrong");
    const shown=cz.split
      ? cz.parts.join(" \u2026 ")
      : plan.ranges[0][0]===cz.i
        ? cz.parts[0]
        : (cz.toks[cz.from]+" "+cz.toks[cz.i]).replace(/[.,;!?]$/,"").trim();
    answer(c,ok,`the sentence uses <b>${esc(shown)}</b>, meaning ${esc(c.trans)}`, ok?1200:2200);
  };
  el.querySelectorAll(".opt").forEach(b=>b.onclick=()=>pick(+b.dataset.k));
  keys({1:()=>pick(0),2:()=>pick(1),3:()=>pick(2),4:()=>pick(3)});
};

STEP.hoeren=()=>{
  if(R.i>=R.q.length)return finish();
  const c=R.q[R.i];
  const opts=shuffle([c].concat(distract(c,3)));
  el.innerHTML=`<div class="stage">${chrome(false)}
    <div class="prompt"><button class="spk" id="spk" aria-label="Play the word">&#9654;</button>
      ${c.art?`<div style="margin-top:12px"><span class="artpill ${c.art}">${c.art}</span></div>`:""}
      <div class="hint" id="h">Tap to hear it again.</div></div>
    <div class="opts">${opts.map((o,k)=>
      `<button class="opt" data-k="${k}"><kbd>${k+1}</kbd>${esc(o.trans)}</button>`).join("")}</div></div>`;
  $("spk").onclick=async()=>{
    if(!await speak(full(c))&&$("h"))$("h").textContent="No speech support on this device.";
  };
  wait(280,async()=>{
    await speak(full(c));
    wait(700,()=>{if(germanVoiceAvailable()===false&&$("h"))
      $("h").innerHTML="No German voice installed here. The word is <b>"+esc(full(c))+"</b>.";});
  });
  let done=false;
  const pick=k=>{
    if(done)return;done=true;
    const ok=opts[k]===c;
    el.querySelectorAll(".opt").forEach((x,j)=>{x.disabled=true;if(opts[j]===c)x.classList.add("right");});
    if(!ok)el.querySelectorAll(".opt")[k].classList.add("wrong");
    answer(c,ok,`<b>${esc(full(c))}</b>`, ok?900:1900);
  };
  el.querySelectorAll(".opt").forEach(b=>b.onclick=()=>pick(+b.dataset.k));
  keys({1:()=>pick(0),2:()=>pick(1),3:()=>pick(2),4:()=>pick(3)});
};

/* ---------- words / add / stats / how ---------- */
const SKLBL={rec:"recognise",gen:"gender",pro:"produce",app:"apply in context"};
let wSearch="",wFolder="all",wHideSusp=false,wSelectMode=false,wSelected=new Set();
/* Panels stay open across re-renders; before this, changing a folder or
   suspending snapped the panel shut, which would make editing unusable. */
let wOpen=new Set(),wEditing=null,editGap=null;
function words(){
  wEditing=null;
  el.innerHTML=`<button class="back" onclick="go('home')">&larr;</button>
  <h2>Your words</h2>
  <input type="text" id="wsearch" placeholder="Search German or English…" value="${esc(wSearch)}">
  <div class="ftabs" id="ftabs"></div>
  <div id="factions"></div>
  <div id="wbar"></div>
  <label class="wtoggle"><input type="checkbox" id="whide" ${wHideSusp?"checked":""}> Hide suspended</label>
  <div id="wlist"></div>`;
  $("wsearch").oninput=e=>{wSearch=e.target.value;renderWordsList();};
  $("whide").onchange=e=>{wHideSusp=e.target.checked;renderWordsList();};
  renderFolderTabs();
  renderWBar();
  renderWordsList();
}
function renderFolderTabs(){
  const names=Array.from(new Set(state.folders.concat(state.cards.map(c=>c.folder).filter(Boolean))))
    .sort((a,b)=>a.localeCompare(b));
  const tabs=[["all","All"],["unsorted","Unsorted"]].concat(names.map(n=>[n,n]));
  $("ftabs").innerHTML=tabs.map(([id,label])=>
    `<button class="ftab ${wFolder===id?"on":""}" onclick="setWFolder('${jsEsc(id)}')">${esc(label)}</button>`
  ).join("")+`<button class="ftab new" onclick="newFolder()">+ Folder</button>`;
  /* Rename / delete only make sense for a real folder, not the All/Unsorted views. */
  const real=wFolder!=="all"&&wFolder!=="unsorted";
  const n=real?state.cards.filter(c=>c.folder===wFolder).length:0;
  const fa=$("factions");
  if(fa)fa.innerHTML=real?`<div class="factions">
      <span class="lede" style="margin:0">${esc(wFolder)}: ${n} word${n===1?"":"s"}</span>
      <button class="ftab" onclick="renameFolder()">Rename</button>
      <button class="ftab danger" onclick="deleteFolder()">Delete folder</button>
    </div>`:"";
}
window.setWFolder=id=>{wFolder=id;renderFolderTabs();renderWordsList();};

window.renameFolder=async()=>{
  const from=wFolder;
  const to=await promptDialog(`Rename "${from}" to`,from,{confirmLabel:"Rename"});
  if(!to||to===from)return;
  if(folderNames(state).includes(to)&&!await confirmDialog(
      `A folder called "${to}" already exists. Merge "${from}" into it?`,{confirmLabel:"Merge"}))return;
  renameFolderIn(state,from,to);
  save();wFolder=to;renderFolderTabs();renderWordsList();
};
window.deleteFolder=async()=>{
  const name=wFolder;
  const words=state.cards.filter(c=>c.folder===name);
  let mode="keep";
  if(words.length){
    mode=await choiceDialog(
      `"${name}" contains ${words.length} word${words.length===1?"":"s"}. What should happen to ${words.length===1?"it":"them"}?`,
      [{label:`Keep ${words.length===1?"it":"them"}, move to Unsorted`,value:"keep"},
       {label:`Delete ${words.length===1?"the word":`all ${words.length} words`} too`,value:"delete",danger:true}],
      {title:`Delete folder "${name}"`});
    if(!mode)return;
    if(mode==="delete"&&!await confirmDialog(
        `This permanently deletes ${words.length} word${words.length===1?"":"s"} and all their progress.`,
        {confirmLabel:"Delete permanently",danger:true}))return;
  }else if(!await confirmDialog(`Delete the empty folder "${name}"?`,{confirmLabel:"Delete",danger:true}))return;

  deleteFolderIn(state,name,mode).forEach(id=>wSelected.delete(id));
  if(pFolder===name)pFolder="";        // custom-session picker shouldn't point at a ghost
  save();wFolder="all";renderFolderTabs();renderWBar();renderWordsList();
};
window.newFolder=async()=>{
  const name=await promptDialog("Folder name");
  if(!name)return;
  if(!state.folders.includes(name))state.folders.push(name);
  save();wFolder=name;renderFolderTabs();renderWordsList();
};
function renderWBar(){
  const bar=$("wbar");if(!bar)return;
  if(!wSelectMode){
    bar.innerHTML=`<div class="selbar"><button class="ftab" onclick="toggleSelectMode()">Select words</button></div>`;
    return;
  }
  const n=wSelected.size;
  bar.innerHTML=`<div class="selbar">
    <span class="wselcount">${n} selected</span>
    <button class="ftab" onclick="selectAllVisible()">All</button>
    <button class="ftab" onclick="clearSelection()">Clear</button>
    <button class="ftab" ${n?"":"disabled"} onclick="bulkSuspend(true)">Suspend</button>
    <button class="ftab" ${n?"":"disabled"} onclick="bulkSuspend(false)">Unsuspend</button>
    ${enrichAvailable()?`<button class="ftab" ${n?"":"disabled"} onclick="bulkResentence()">New sentences</button>`:""}
    <button class="ftab danger" ${n?"":"disabled"} onclick="bulkDelete()">Delete</button>
    <button class="ftab" onclick="toggleSelectMode()">Done</button></div>
    <p class="lede" id="wmsg" style="margin:4px 0 0"></p>`;
}
window.toggleSelectMode=()=>{wSelectMode=!wSelectMode;wSelected.clear();wEditing=null;renderWBar();renderWordsList();};
window.clearSelection=()=>{wSelected.clear();renderWBar();renderWordsList();};
window.selectAllVisible=()=>{filteredWords().forEach(c=>wSelected.add(c.id));renderWBar();renderWordsList();};
window.bulkSuspend=flag=>{
  state.cards.forEach(c=>{
    if(!wSelected.has(c.id))return;
    c.suspended=flag;
    if(!flag&&c.leech){c.leech=false;c.lapses=0;}
  });
  save();renderWBar();renderWordsList();
};
/* Regenerate example sentences for the selection, in batches of 20 (the
   Worker's limit). Only the sentence may change. */
window.bulkResentence=async()=>{
  const picked=state.cards.filter(c=>wSelected.has(c.id));
  if(!picked.length)return;
  const msg=()=>$("wmsg");
  let changed=0,failed=null;
  for(let i=0;i<picked.length;i+=20){
    msg()&&(msg().textContent=`Writing new sentences\u2026 ${Math.min(i+20,picked.length)} of ${picked.length}`);
    try{
      const results=await enrichWords(picked.slice(i,i+20));
      const log=applyEnrichment(state,results,{only:["example"]});
      state.enrichLog=log.concat(state.enrichLog||[]).slice(0,60);
      changed+=log.filter(e=>e.changes.length).length;
      save();
    }catch(e){failed=enrichMessage(e&&e.code,e&&e.detail);break;}
  }
  renderWordsList();
  msg()&&(msg().textContent=failed
    ?`${changed} updated, then stopped: ${failed}`
    :`New sentences for ${changed} word${changed===1?"":"s"}. See what changed under Add words.`);
};
window.bulkDelete=async()=>{
  const n=wSelected.size;if(!n)return;
  if(!await confirmDialog(`Delete ${n} word${n===1?"":"s"}? This can't be undone.`,
    {confirmLabel:"Delete",danger:true}))return;
  state.cards=state.cards.filter(c=>!wSelected.has(c.id));
  wSelected.clear();save();renderFolderTabs();renderWBar();renderWordsList();
};
function filteredWords(){
  const q=wSearch.trim().toLowerCase();
  return state.cards.filter(c=>{
    if(wFolder==="unsorted"&&c.folder)return false;
    if(wFolder!=="all"&&wFolder!=="unsorted"&&c.folder!==wFolder)return false;
    if(wHideSusp&&c.suspended)return false;
    if(q&&!(c.term.toLowerCase().includes(q)||c.trans.toLowerCase().includes(q)))return false;
    return true;
  }).sort((a,b)=>stage(a)-stage(b)||a.term.localeCompare(b.term,"de"));
}
function renderWordsList(){
  const list=filteredWords();
  const folderOpts=Array.from(new Set(state.folders.concat(state.cards.map(x=>x.folder).filter(Boolean)))).sort();
  $("wlist").innerHTML=!list.length?`<p class="lede" style="margin-top:16px">No words match.</p>`
    :`<div class="list">${list.map(c=>{
      const st=stage(c), sel=wSelected.has(c.id);
      const onclick=wSelectMode?`toggleSelectRow('${c.id}')`:`toggleDetail('${c.id}')`;
      return`<button class="item wrow ${c.suspended?"susp":""}" onclick="${onclick}">
        <div class="wtop">
          ${wSelectMode?`<span class="chk ${sel?"on":""}"></span>`:""}
          <span class="sq" style="background:var(--s${st})" title="now at ${STAGES[st].n}"></span>
          ${c.peak>st?`<span class="sqpeak" style="border-color:var(--s${c.peak})" title="reached ${STAGES[c.peak].n} before"></span>`:""}
          <span class="a ${c.art||""}">${c.art||""}</span><span>${esc(displayTerm(c))}</span>
          ${c.leech?'<span class="badge">leech</span>':""}
          ${c.suspended?'<span class="t" style="color:var(--no)">suspended</span>':`
          <span class="mini"><span style="width:${Math.min(1,c.lp/GRADUATE)*100}%"></span></span>
          <span class="t" style="margin-left:11px">${when(c)}</span>`}
        </div>
        <div class="wmeta">${esc(c.trans)}${c.folder?` · ${esc(c.folder)}`:""}</div>
      </button>
      <div class="detail ${wOpen.has(c.id)&&!wSelectMode?"on":""}" id="d-${c.id}">
      ${wEditing===c.id?wordEditor(c):wordSummary(c)}
      ${c.peak>st?`<div class="drow"><span>best stage</span><span style="flex:1;color:var(--ink)">${STAGES[c.peak].n}, now back at ${STAGES[st].n}</span></div>`:""}
      ${["rec","gen","pro","app"].map(k=>{
        if(k==="gen"&&!c.art)return"";
        return`<div class="drow"><span>${SKLBL[k]}</span>
          <span class="dbar"><span style="width:${Math.min(1,c.sk[k]/MET)*100}%"></span></span>
          <span class="dn">${c.sk[k].toFixed(1)} / ${MET.toFixed(1)}</span></div>`;
      }).join("")}${c.lapses?`<div class="drow"><span>forgotten</span><span></span><span class="dn">${c.lapses}×</span></div>`:""}
      <div class="drow" style="margin-top:6px;gap:8px">
        <span>folder</span>
        <select onchange="setWordFolder('${c.id}',this.value)" style="flex:1;font:inherit;background:var(--surface);color:var(--ink);border:1px solid var(--line)">
          <option value="" ${!c.folder?"selected":""}>Unsorted</option>
          ${folderOpts.map(f=>`<option value="${esc(f)}" ${c.folder===f?"selected":""}>${esc(f)}</option>`).join("")}
        </select></div>
      <button class="go ghost" style="margin-top:8px" onclick="toggleSuspend('${c.id}')">${c.suspended?"Resume reviewing":"Suspend: don't quiz me on this yet"}</button>
      <button class="go ghost danger" style="margin-top:6px" onclick="deleteWord('${c.id}')">Delete this word</button>
      </div>`;
    }).join("")}</div>`;
}
window.toggleDetail=id=>{
  const d=$("d-"+id);if(!d)return;
  if(wOpen.has(id)){wOpen.delete(id);if(wEditing===id)wEditing=null;renderWordsList();return;}
  wOpen.add(id);d.classList.add("on");
};

/* ---------- viewing and editing a word ---------- */

/* Lückentext preview for any card-like object: the sentence with the gap
   drawn in, or a plain explanation of why that word won't appear there. */
function gapPreview(c){
  const {cz,reason}=clozeStatus(c);
  if(reason==="empty")return`<span class="gapwarn">No sentence yet, so Lückentext skips this word.</span>`;
  if(reason==="missing")return`<span class="gapwarn">The sentence doesn't contain "${esc(c.term)}", so Lückentext can't make a gap from it.</span>`;
  if(reason==="short")return`<span class="gapwarn">Too little of the sentence is left around the word to guess it fairly. Add a few words.</span>`;
  const plan=planCloze(c,cz);
  return`<span class="gapok">Lückentext shows:</span> ${clozeHtml(cz,plan.ranges)}${
    cz.split?' <span class="wfhint" style="display:block">Two gaps: this verb splits.</span>':""}`;
}

function wordSummary(c){
  const type={n:"noun",v:"verb",a:"adjective"}[c.pos]||"word";
  return`<div class="wsum">
    <div class="drow"><span>meaning</span><span class="wval">${esc(c.trans)}</span></div>
    <div class="drow"><span>type</span><span class="wval">${type}${c.pos==="n"
      ?` · ${c.art?`<b class="a ${c.art}">${c.art}</b>`:'<span class="gapwarn">no article</span>'}`
      :((c.sep||detectSeparable(c.term))?` · splits as ${esc(displayTerm(c))}`:"")}</span></div>
    ${c.pos==="n"?`<div class="drow"><span>plural</span><span class="wval">${
      pluralForm(c)?`die ${esc(pluralForm(c))}`:'<span class="gapwarn">not set, so Mehrzahl skips this word</span>'}</span></div>`:""}
    <div class="drow"><span>sentence</span><span class="wval">${c.ex?esc(c.ex):'<span class="gapwarn">none</span>'}</span></div>
    <div class="gapbox">${gapPreview(c)}</div>
    <button class="ftab" style="margin-top:8px" onclick="editWord('${c.id}')">Edit</button>
  </div>`;
}

function wordEditor(c){
  const id=c.id;
  return`<div class="wedit">
    <label class="wf"><span>German</span>
      <input type="text" id="e-term-${id}" value="${esc(c.term)}" oninput="editPreview('${id}')" autocomplete="off" spellcheck="false"></label>
    <label class="wf"><span>Meaning</span>
      <input type="text" id="e-trans-${id}" value="${esc(c.trans)}" autocomplete="off"></label>
    <div class="wfrow">
      <label class="wf"><span>Type</span>
        <select id="e-pos-${id}" onchange="editPreview('${id}')">
          ${[["n","noun"],["v","verb"],["a","adjective"]].map(([v,l])=>`<option value="${v}" ${c.pos===v?"selected":""}>${l}</option>`).join("")}
        </select></label>
      <label class="wf"><span>Article</span>
        <select id="e-art-${id}">
          ${[["","none"],["der","der"],["die","die"],["das","das"]].map(([v,l])=>`<option value="${v}" ${(c.art||"")===v?"selected":""}>${l}</option>`).join("")}
        </select></label>
      <label class="wf"><span>Plural</span>
        <input type="text" id="e-plural-${id}" value="${esc(c.plural||"")}" oninput="editPreview('${id}')" autocomplete="off" spellcheck="false"></label>
    </div>
    <p class="wfhint" id="e-plhint-${id}">${pluralHint(c)}</p>
    <label class="wf"><span>Example sentence</span>
      <textarea id="e-ex-${id}" rows="3" oninput="editPreview('${id}')">${esc(c.ex||"")}</textarea></label>
    <div class="gapbox" id="e-prev-${id}">${gapPreview(c)}</div>
    <div class="gappick" id="e-pick-${id}">${gapChips({...c,gap:editGap})}</div>
    ${enrichAvailable()?`<button class="ftab" style="margin-top:6px" id="e-ai-${id}" onclick="suggestSentence('${id}')">Suggest a sentence with AI</button>`:""}
    <p class="wfhint" id="e-msg-${id}"></p>
    <div class="modal-actions" style="margin-top:6px">
      <button class="go ghost" onclick="cancelEdit()">Cancel</button>
      <button class="go" onclick="saveWordEdit('${id}')">Save</button>
    </div>
  </div>`;
}

/* One tappable chip per word of the sentence, so the gap can be chosen by
   hand when the automatic match misses (ging for gehen) or picks wrong. */
function gapChips(c){
  const words=String(c.ex||"").split(/\s+/).filter(t=>t.replace(/[^\p{L}]/gu,""));
  if(!words.length)return"";
  const cur=clozeStatus(c).cz;
  const curW=cur?cur.w.toLowerCase():null;
  return`<span class="wfhint" style="display:block;margin:6px 0 4px">Wrong word hidden, or none? Tap the one Lückentext should hide:</span>
    ${words.map((t,k)=>{const w=t.replace(/[^\p{L}]/gu,"");
      return`<button type="button" class="chip ${curW&&w.toLowerCase()===curW?"on":""}" onclick="pickGap('${c.id}',${k})">${esc(w)}</button>`;}).join("")}
    ${c.gap?`<button type="button" class="chip clear" onclick="pickGap('${c.id}',-1)">automatic</button>`:""}`;
}
window.pickGap=(id,k)=>{
  if(k<0)editGap=null;
  else{
    const words=($(`e-ex-${id}`).value||"").split(/\s+/).filter(t=>t.replace(/[^\p{L}]/gu,""));
    editGap=words[k]?words[k].replace(/[^\p{L}]/gu,""):null;
  }
  editPreview(id);
};

/* Spell out what the plural shorthand expands to, so "-e" is not a mystery. */
function pluralHint(v){
  if(v.pos!=="n")return "Article and plural only apply to nouns.";
  const pl=pluralForm(v);
  if(!pl)return "Shorthand works here: -e, -en, -n, -s, \u00a8er, or - if the plural is the same.";
  return "Plural: die "+pl;
}

/* read the form into a card-shaped object */
function editValues(id){
  const v=k=>{const n=$(`e-${k}-${id}`);return n?n.value.trim():"";};
  return{id,term:v("term"),trans:v("trans"),pos:v("pos")||"n",art:v("art")||null,plural:v("plural"),ex:v("ex"),gap:editGap};
}
window.editWord=id=>{
  const c=state.cards.find(x=>x.id===id);
  editGap=c&&c.gap||null;
  wEditing=id;wOpen.add(id);renderWordsList();$(`e-ex-${id}`)?.focus();
};
window.cancelEdit=()=>{wEditing=null;renderWordsList();};
window.editPreview=id=>{
  const v=editValues(id);
  /* a picked word that's no longer in the sentence reverts to automatic */
  if(editGap&&!v.ex.split(/\s+/).some(t=>t.replace(/[^\p{L}]/gu,"").toLowerCase()===editGap.toLowerCase())){
    editGap=null;v.gap=null;
  }
  const box=$(`e-prev-${id}`);if(box)box.innerHTML=gapPreview(v);
  const ph=$(`e-plhint-${id}`);if(ph)ph.textContent=pluralHint(v);
  const pick=$(`e-pick-${id}`);if(pick)pick.innerHTML=gapChips(v);
};
window.saveWordEdit=id=>{
  const c=state.cards.find(x=>x.id===id);if(!c)return;
  const v=editValues(id), msg=$(`e-msg-${id}`);
  if(!v.term||!v.trans){if(msg)msg.textContent="The German word and its meaning can't be empty.";return;}
  /* strip an article typed into the word itself ("das Haus") into the article field */
  /* accept "der Bleistift, -e" or "ab-sagen" typed straight into the field */
  const g=parseGermanField(v.term);
  v.term=g.term;
  if(g.art){v.art=v.art||g.art;v.pos="n";}
  if(g.plural&&!v.plural)v.plural=g.plural;
  if(g.sep){v.pos="v";c.sep=g.sep;}else delete c.sep;
  c.term=v.term;c.trans=v.trans;c.pos=v.pos;c.ex=v.ex;
  c.art=v.pos==="n"?v.art:null;
  c.plural=v.pos==="n"?v.plural:"";
  /* only keep a hand-picked gap if the automatic match wouldn't find the same word */
  if(v.gap){
    const auto=clozeStatus({term:c.term,pos:c.pos,ex:c.ex}).cz;
    if(auto&&auto.w.toLowerCase()===v.gap.toLowerCase())delete c.gap; else c.gap=v.gap;
  }else delete c.gap;
  c.editedAt=Date.now();
  /* Progress is kept on purpose: fixing a typo or a sentence shouldn't cost
     the word its history. */
  save();wEditing=null;renderWordsList();
};
window.suggestSentence=async id=>{
  const btn=$(`e-ai-${id}`),msg=$(`e-msg-${id}`),v=editValues(id);
  if(!v.term||!v.trans){if(msg)msg.textContent="Fill in the word and its meaning first.";return;}
  if(btn)btn.disabled=true;if(msg)msg.textContent="Writing a sentence\u2026";
  try{
    const [r]=await enrichWords([{id,term:v.term,trans:v.trans}]);
    if(r&&r.ex){
      /* goes into the box, not the word: nothing changes until you press Save */
      $(`e-ex-${id}`).value=r.ex;editGap=null;editPreview(id);
      if(msg)msg.textContent="Suggestion added. Edit it if you like, then Save.";
    }else if(msg)msg.textContent="No sentence came back. Try again.";
  }catch(e){if(msg)msg.textContent=enrichMessage(e&&e.code,e&&e.detail);}
  if(btn)btn.disabled=false;
};

window.toggleSelectRow=id=>{
  if(wSelected.has(id))wSelected.delete(id);else wSelected.add(id);
  renderWBar();renderWordsList();
};
window.setWordFolder=(id,val)=>{
  const c=state.cards.find(x=>x.id===id);if(!c)return;
  c.folder=val||null;save();renderFolderTabs();renderWordsList();
};
window.toggleSuspend=id=>{
  const c=state.cards.find(x=>x.id===id);if(!c)return;
  c.suspended=!c.suspended;
  /* resuming a leech gives it a clean slate, otherwise it re-leeches on the very next miss */
  if(!c.suspended&&c.leech){c.leech=false;c.lapses=0;}
  save();renderWordsList();
};
window.deleteWord=async id=>{
  const c=state.cards.find(x=>x.id===id);if(!c)return;
  if(!await confirmDialog(`Delete "${c.term}"? This can't be undone.`,
    {confirmLabel:"Delete",danger:true}))return;
  state.cards=state.cards.filter(x=>x.id!==id);
  save();renderFolderTabs();renderWordsList();
};
function add(){
  const folderOpts=Array.from(new Set(state.folders.concat(state.cards.map(x=>x.folder).filter(Boolean)))).sort();
  el.innerHTML=`<button class="back" onclick="go('home')">&larr;</button>
  <h2>Add words</h2>
  <p class="lede">One per line: German, then a tab or semicolon, then the English. A third field becomes the example sentence, which unlocks Lückentext. A fourth field names a folder. Articles are picked up automatically, and so is dictionary notation: write "der Bleistift, -e" or "das Haus, \u00a8er" for the plural, and "ab-sagen" for a separable verb.</p>
  <div class="uprow">
    <label class="upbtn">Choose a file
      <input type="file" id="fileIn" accept=".csv,.tsv,.txt,text/plain,text/csv" hidden></label>
    <span class="lede" id="fname" style="margin:0">CSV, TSV or TXT, or just paste below</span>
  </div>
  <textarea id="ta" rows="7" placeholder="die Wohnung; flat; Die Wohnung ist leider viel zu teuer.; Housing&#10;begegnen; to encounter"></textarea>
  <div class="drow" style="gap:8px;margin:8px 0">
    <span style="width:78px;flex:none;font-size:13px;color:var(--muted)">add to</span>
    <select id="tgtFolder" style="flex:1;font:inherit;background:var(--surface);color:var(--ink);border:1px solid var(--line);padding:8px">
      <option value="">Unsorted (or whatever the 4th field says)</option>
      ${folderOpts.map(f=>`<option value="${esc(f)}">${esc(f)}</option>`).join("")}
      <option value="__new__">+ New folder…</option>
    </select></div>
  <button class="go" id="imp">Add to my words</button>
  <p class="lede" id="msg" style="margin-top:14px"></p>
  <div id="enrichBox"></div>
  <div id="enrichLog"></div>`;

  const tgt=$("tgtFolder");
  let lastTgt=tgt.value;
  tgt.onchange=async()=>{
    if(tgt.value!=="__new__"){lastTgt=tgt.value;return;}
    const name=await promptDialog("New folder name",'',{confirmLabel:"Create"});
    if(!name){tgt.value=lastTgt;return;}
    if(!state.folders.includes(name)){state.folders.push(name);save();}
    /* insert before the "+ New folder" option unless it's already listed */
    if(![...tgt.options].some(o=>o.value===name)){
      const o=document.createElement("option");o.value=name;o.textContent=name;
      tgt.insertBefore(o,tgt.querySelector('option[value="__new__"]'));
    }
    tgt.value=name;lastTgt=name;
  };
  $("fileIn").onchange=e=>{
    const f=e.target.files&&e.target.files[0];if(!f)return;
    const r=new FileReader();
    r.onload=()=>{
      let txt=String(r.result||"").replace(/^\uFEFF/,"");   /* strip BOM that Excel exports add */
      /* a comma-separated file with no tabs or semicolons: treat commas as the separator */
      if(!/[\t;]/.test(txt)&&/,/.test(txt))txt=txt.split("\n").map(l=>l.split(",").join(";")).join("\n");
      $("ta").value=txt.trim();
      $("fname").textContent=`${f.name} loaded, ${txt.trim().split("\n").filter(Boolean).length} lines`;
    };
    r.onerror=()=>{$("fname").textContent="Couldn't read that file.";};
    r.readAsText(f);
  };

  $("imp").onclick=()=>{
    const tv=$("tgtFolder").value;
    const tgt=(tv&&tv!=="__new__")?tv:null;
    const lines=$("ta").value.split("\n").map(l=>l.trim()).filter(Boolean);let n=0;
    lines.forEach(l=>{
      /* A line with tabs is tab-separated (our own export, Anki's plain-text
         export): split on tabs ONLY, or a semicolon inside a sentence splits
         it in two and shifts the folder into the wrong column. */
      const p=(l.includes("\t")?l.split("\t"):l.split(/;|\s\|\s/)).map(x=>x.trim());
      while(p.length&&!p[p.length-1])p.pop();   // trailing empties only; keep an empty sentence column in place
      if(p.length<2||!p[0]||!p[1])return;   // need at least a German word and a meaning
      const g=parseGermanField(p[0]);
      const folder=tgt||p[3]||null;
      if(folder&&!state.folders.includes(folder))state.folders.push(folder);
      state.cards.push({id:"u"+now()+Math.random().toString(36).slice(2,6),
        art:g.art,term:g.term,sep:g.sep||undefined,plural:g.plural,trans:p[1],ex:p[2]||"",
        pos:g.art?"n":"v",ease:2.5,interval:0,reps:0,lapses:0,due:0,seen:0,lp:0,
        suspended:false,folder,peak:0,leech:false,
        sk:{rec:0,gen:0,pro:0,app:0},skd:{rec:{d:null,e:0},gen:{d:null,e:0},pro:{d:null,e:0},app:{d:null,e:0}}});
      n++;
    });
    save();
    $("msg").textContent=n?`Added ${n} word${n===1?"":"s"}.`:"No usable lines. Check that each has a separator.";
    $("ta").value="";$("fname").textContent="CSV, TSV or TXT, or just paste below";
    renderEnrich();
  };
  renderEnrich();
  renderEnrichLog();
}

/* ---------- AI enrichment ---------- */
const needsEnrich=c=>(c.pos==="n"&&!c.art)||!c.ex||(c.pos==="n"&&!c.plural);
async function renderEnrich(){
  const box=$("enrichBox");if(!box)return;
  const pending=state.cards.filter(needsEnrich);
  if(!pending.length||!enrichAvailable()){box.innerHTML="";return;}
  box.innerHTML=`<h3>Fill in the gaps</h3>
    <p class="lede">${pending.length} word${pending.length===1?" is":"s are"} missing an article, plural or example sentence. AI can fill those in, which is what unlocks Artikel and L\u00fcckentext for them.</p>
    <button class="go" id="enrichGo">Fill in ${Math.min(pending.length,20)} with AI</button>
    <p class="lede" id="enrichMsg" style="margin-top:10px"></p>`;
  $("enrichGo").onclick=()=>runEnrich(pending.slice(0,20));
}
async function runEnrich(batch){
  const btn=$("enrichGo"),msg=$("enrichMsg");
  btn.disabled=true;msg.textContent="Working\u2026";
  try{
    const results=await enrichWords(batch);
    const log=applyEnrichment(state,results);
    state.enrichLog=log.concat(state.enrichLog||[]).slice(0,60);
    save();
    msg.textContent=log.length?`Updated ${log.length} word${log.length===1?"":"s"}.`:"Nothing needed changing.";
    renderEnrich();
    renderEnrichLog();
  }catch(e){
    msg.textContent=enrichMessage(e&&e.code, e&&e.detail);
    if(btn)btn.disabled=false;
  }
}
const FIELDLBL={article:"article",plural:"plural",example:"example sentence",type:"word type"};
function renderEnrichLog(){
  const box=$("enrichLog");if(!box)return;
  const log=state.enrichLog||[];
  if(!log.length){box.innerHTML="";return;}
  box.innerHTML=`<h3>What the AI changed</h3>
    <p class="lede">Most recent first, so you can spot-check it rather than trusting it. Anything wrong can be fixed under Your words: open the word and tap Edit.</p>
    ${log.slice(0,25).map(e=>`
      <div class="elog">
        <div class="elogterm">${esc(e.term)}</div>
        ${e.changes.map(ch=>`
          <div class="elogrow">
            <span class="elogf">${esc(FIELDLBL[ch.field]||ch.field)}</span>
            <span class="elogfrom">${esc(ch.from)}</span>
            <span class="elogarrow">\u2192</span>
            <span class="elogto">${esc(ch.to)}</span>
          </div>`).join("")}
        ${e.rejected.map(r=>`<div class="elogrow elogrej">rejected: ${esc(r)}</div>`).join("")}
      </div>`).join("")}
    <button class="go ghost" onclick="clearEnrichLog()">Clear this list</button>`;
}
window.clearEnrichLog=()=>{state.enrichLog=[];save();renderEnrichLog();};

function stats(){
  const c=state.cards.filter(x=>!x.suspended),b=[0,0,0,0,0,0];c.forEach(x=>b[stage(x)]++);
  const learning=c.filter(x=>x.reps&&x.lp<GRADUATE).length;
  const susp=state.cards.length-c.length;
  const gen=["der","die","das"].map(a=>{
    const all=c.filter(x=>x.art===a);return{a,n:all.filter(x=>x.sk.gen>=MET).length,t:all.length};});
  el.innerHTML=`<button class="back" onclick="go('home')">&larr;</button>
  <h2>Progress</h2><p class="lede">A word moves up a stage once it has ${MET.toFixed(1)} credit in the skill that stage tests.${susp?` ${susp} suspended word${susp===1?" isn't":"s aren't"} counted here.`:""}</p>
  <div class="list">${STAGES.map((s,i)=>`<div class="item">
    <span class="sq" style="background:var(--s${i})"></span><span>${s.n}</span>
    <span style="color:var(--muted);font-size:13px">${s.e}</span>
    <span class="t">${b[i]}</span></div>`).join("")}</div>
  <h3>Gender</h3><p class="lede">Nouns whose article has reached full credit.</p>
  <div class="list">${gen.map(g=>`<div class="item">
    <span class="a ${g.a}">${g.a}</span><span style="color:var(--muted);font-size:13px">${g.t} nouns</span>
    <span class="t">${g.n} / ${g.t}</span></div>`).join("")}</div>
  <div class="stats" style="margin-top:26px">
    <div class="stat"><b>${learning}</b><small>still in the learning phase</small></div>
    <div class="stat"><b>${state.reviews||0}</b><small>answers given</small></div>
  </div>`;
}
function how(){
  el.innerHTML=`<button class="back" onclick="go('home')">&larr;</button>
  <h2>How the scoring works</h2>
  <p class="lede">Two separate things move when you answer, and neither treats a guess like real knowledge.</p>
  <h3>Credit per answer</h3>
  <p class="lede">Each game is worth what it actually proves. A one-in-two guess can't count for as much as typing a word from nothing.</p>
  <div class="list">${Object.keys(GAMES).map(k=>`<div class="item">
    <span>${GAMES[k].t}</span><span style="color:var(--muted);font-size:13px">${GAMES[k].odds}</span>
    <span class="t">+${GAMES[k].w.toFixed(1)}</span></div>`).join("")}</div>
  <h3>Learning phase</h3>
  <p class="lede">A new word gets no day-scale interval until it has ${GRADUATE.toFixed(1)} credit behind it. Before that it comes back after 1, then 9, then 25 minutes, so a lucky guess buys minutes, not a day. Get a graduated word wrong and it drops back here.</p>
  <h3>Stage promotion</h3>
  <p class="lede">Every word holds four separate scores: recognition, gender, production, use in context. A stage is just "the first score that's still below ${MET.toFixed(1)}," checked in that order, so a noun can't reach Produzieren on recognition alone, it needs gender met too. Non-nouns skip the gender check. Getting two different scores to ${MET.toFixed(1)} in one sitting is fine and can jump a word two stages at once. The same-day discount only slows down repeating the <i>same</i> skill, not different skills. A wrong answer costs the skill a full point.</p>
  <h3>Stage vs. review: two different clocks</h3>
  <p class="lede">Stage only decides which game a word is likely to turn up in, and what the home screen suggests. It has no say over whether the word gets reviewed. Every word also has its own due date, running separately, and a due word is always pulled to the front of the next game you open regardless of its stage. A word sitting at Sicher still comes back the moment its interval runs out.</p>
  <h3>Best stage</h3>
  <p class="lede">A single wrong answer can knock a word's live stage back a long way, because the stage is only ever "the first score still short." So every word also keeps a high-water mark that never falls. When the two differ, Your words shows the live stage as a filled square and the best it ever reached as a hollow one, so a bad day doesn't erase the record of what you'd already learned.</p>
  <button class="go ghost" onclick="go('settings')">Settings</button>
  <button class="go ghost" onclick="go('home')">Back</button>`;
}

/* ---------- custom study sessions (#4) ---------- */
let pFolder="", pPractice=true;
function practice(){
  const folders=Array.from(new Set(state.cards.map(c=>c.folder).filter(Boolean))).sort();
  const scoped=k=>playable(k,pFolder||undefined);
  el.innerHTML=`<button class="back" onclick="go('home')">&larr;</button>
  <h2>Custom session</h2>
  <p class="lede">Drill a specific folder without disturbing the schedule you've built up. In practice mode nothing is graded: no credit, no interval changes, no lapses counted.</p>

  <h3>Folder</h3>
  <div class="ftabs">
    <button class="ftab ${pFolder===""?"on":""}" onclick="setPFolder('')">All words</button>
    ${folders.map(f=>`<button class="ftab ${pFolder===f?"on":""}" onclick="setPFolder('${jsEsc(f)}')">${esc(f)}</button>`).join("")}
  </div>

  <h3>Mode</h3>
  <div class="ftabs">
    <button class="ftab ${pPractice?"on":""}" onclick="setPMode(true)">Practice (no effect on schedule)</button>
    <button class="ftab ${!pPractice?"on":""}" onclick="setPMode(false)">Graded (counts normally)</button>
  </div>
  <p class="lede">${pPractice
    ? "Nothing you do in this round will be saved against your words."
    : "This round counts exactly like playing from the home screen, but limited to the folder above."}</p>

  <h3>Game</h3>
  <div class="tiles">
    ${Object.keys(GAMES).map(k=>{const n=scoped(k);return`
      <button class="tile ${n?"":"dim"}" ${n?`onclick="startSession('${k}')"`:"disabled"}>
        <div><b>${GAMES[k].t}</b><small>${GAMES[k].d}</small></div>
        <span class="cnt">${n}</span></button>`;}).join("")}
  </div>`;
}
window.setPFolder=f=>{pFolder=f;practice();};
window.setPMode=v=>{pPractice=v;practice();};
window.startSession=k=>{setSession(pFolder,pPractice);go(k);};

/* ---------- #5 retention matrix + SRS health ---------- */
function health(){
  const active=state.cards.filter(c=>!c.suspended);
  const bal=reviewBalance();
  const fc=forecast(14);
  const peak=Math.max(1,...fc);
  const counts={};RETENTION_BANDS.forEach(b=>counts[b.key]=0);
  active.forEach(c=>counts[bandOf(c).key]++);
  const done=todayCount(), cap=state.dailyCap;

  /* matrix cells sorted strongest-first so the shape of the collection reads
     at a glance rather than being scattered */
  const cells=active.slice().sort((a,b)=>retention(b)-retention(a));

  el.innerHTML=`<button class="back" onclick="go('home')">&larr;</button>
  <h2>Retention</h2>
  <p class="lede">Each square is one word, strongest first. Retention blends how long its interval has grown, how balanced its four skills are, how often you've forgotten it, and how overdue it is.</p>
  <div class="matrix">${cells.map(c=>{
    const b=bandOf(c);
    return `<span class="cell band-${b.key}" title="${esc(c.term)}: ${b.label}"></span>`;
  }).join("")}</div>
  <div class="legend2">${RETENTION_BANDS.map(b=>
    `<span><i class="band-${b.key}"></i>${b.label} <b>${counts[b.key]}</b></span>`).join("")}</div>

  <h3>Review balance</h3>
  <p class="lede">${bal.overdue>bal.dueToday*2&&bal.overdue>10
    ? "Your overdue pile is well ahead of today's load. Clearing it beats learning new words for a few days."
    : bal.overdue===0?"Nothing overdue. This is the healthy state."
    : "A small overdue pile is normal; it only matters when it keeps growing."}</p>
  <div class="list">
    <div class="item"><span class="sq band-fragile"></span><span>Overdue</span><span class="t">${bal.overdue}</span></div>
    <div class="item"><span class="sq band-shaky"></span><span>Due today</span><span class="t">${bal.dueToday}</span></div>
    <div class="item"><span class="sq band-solid"></span><span>Scheduled ahead</span><span class="t">${bal.upcoming}</span></div>
    <div class="item"><span class="sq band-new"></span><span>Still learning</span><span class="t">${bal.learning}</span></div>
    <div class="item"><span class="sq" style="background:var(--line)"></span><span>Suspended</span><span class="t">${bal.suspended}</span></div>
  </div>

  <h3>Next two weeks</h3>
  <p class="lede">What you've already committed to by answering correctly. Tall bars ahead mean a heavy day coming.</p>
  <div class="fc">${fc.map((n,i)=>`
    <span class="fcbar" title="${n} due">
      <i style="height:${Math.round(n/peak*100)}%"></i>
      <em>${i===0?"now":(i%7===0?"+"+i:"")}</em>
    </span>`).join("")}</div>

  <h3>Daily cap</h3>
  <p class="lede">A ceiling on how many different words you touch per day, so a backlog can't turn into a two-hour session. Practice rounds don't count toward it.</p>
  <div class="drow" style="gap:10px;margin-bottom:6px">
    <span style="width:auto;flex:none;font-size:14px;color:var(--ink)">Stop me after</span>
    <input type="range" id="capR" min="0" max="300" step="10" value="${cap}" style="flex:1">
    <span class="dn" id="capN">${cap?cap+" words":"no cap"}</span></div>
  <div class="capbar"><span style="width:${cap?Math.min(100,done/cap*100):0}%"></span></div>
  <p class="lede">${done} word${done===1?"":"s"} reviewed today${cap?` of ${cap}`:""}.</p>`;

  const r=$("capR");
  r.oninput=e=>{$("capN").textContent=+e.target.value?e.target.value+" words":"no cap";};
  r.onchange=e=>{state.dailyCap=+e.target.value;save();};
}

function settings(){
  const leeches=state.cards.filter(c=>c.leech).length;
  el.innerHTML=`<button class="back" onclick="go('home')">&larr;</button>
  <h2>Settings</h2>
  <h3>Leeches</h3>
  <p class="lede">A word you keep forgetting will otherwise cycle back forever. After this many lapses it gets set aside automatically so it stops crowding your rounds, and you can deal with it deliberately instead. Resuming one from Your words clears its count and puts it back in rotation.</p>
  <div class="drow" style="gap:10px;margin-bottom:6px">
    <span style="width:auto;flex:none;font-size:14px;color:var(--ink)">Set aside after</span>
    <input type="range" id="leechR" min="0" max="15" step="1" value="${state.leechAt}" style="flex:1">
    <span class="dn" id="leechN">${state.leechAt?state.leechAt+" lapses":"never"}</span></div>
  <p class="lede">${leeches?`${leeches} word${leeches===1?"":"s"} currently set aside.`:"Nothing set aside right now."}</p>
  <h3>Sound</h3>
  <button class="go ghost" onclick="toggleSound();go('settings')">${state.sound?"Sound is on":"Sound is off"}</button>
  <h3>Your data</h3>
  <p class="lede">Everything lives in this browser only. Export a copy you can keep, or restore one.</p>
  <button class="go ghost" onclick="exportData()">Export full backup (words, folders and progress)</button>
  <button class="go ghost" onclick="exportWordList()">Export word list (spreadsheet, re-importable)</button>
  <label class="go ghost" style="display:block;cursor:pointer">Restore from a backup
    <input type="file" id="restoreIn" accept=".json,application/json" hidden></label>
  <p class="lede" id="dataMsg" style="margin-top:10px"></p>
  <h3>Intro</h3>
  <button class="go ghost" onclick="state.onboarded=false;save();go('home')">Show the intro again</button>`;
  const r=$("leechR");
  r.oninput=e=>{$("leechN").textContent=+e.target.value?e.target.value+" lapses":"never";};
  r.onchange=e=>{state.leechAt=+e.target.value;save();};
  $("restoreIn").onchange=e=>{
    const f=e.target.files&&e.target.files[0];if(!f)return;
    const rd=new FileReader();
    rd.onload=async()=>{
      try{
        const d=JSON.parse(String(rd.result));
        if(!d||!Array.isArray(d.cards)||!d.cards.length)throw new Error("no cards");
        if(!await confirmDialog(`Replace everything with this backup (${d.cards.length} words)?`,
          {confirmLabel:"Restore"}))return;
        delete d._summary;
        localStorage.setItem(KEY,JSON.stringify(d));
        setState(load());go("home");
      }catch(err){$("dataMsg").textContent="That file didn't look like a Vokabelspiel backup.";}
    };
    rd.readAsText(f);
  };
}
function download(filename,text,type){
  const blob=new Blob([text],{type});
  const a=document.createElement("a");
  a.href=URL.createObjectURL(blob);
  a.download=filename;
  document.body.appendChild(a);a.click();
  setTimeout(()=>{URL.revokeObjectURL(a.href);a.remove();},0);
}
const stamp=()=>new Date().toISOString().slice(0,10);

/* Full backup. Pretty-printed with a summary block up top, so opening the
   file shows at a glance what's in it, folders included. The summary is
   ignored on restore; everything restore needs is in the normal fields. */
window.exportData=()=>{
  try{
    const byFolder={};
    state.cards.forEach(c=>{const k=c.folder||"(Unsorted)";byFolder[k]=(byFolder[k]||0)+1;});
    state.folders.forEach(f=>{if(!(f in byFolder))byFolder[f]=0;});
    const out={
      _summary:{
        app:"Vokabelspiel",exportedAt:new Date().toISOString(),
        words:state.cards.length,folders:byFolder,
        note:"Full backup: restore it from Settings. Each word's folder is in its \"folder\" field."
      },
      ...state
    };
    download(`vokabelspiel-backup-${stamp()}.json`,JSON.stringify(out,null,2),"application/json");
    $("dataMsg")&&($("dataMsg").textContent="Backup saved to your downloads.");
  }catch(e){$("dataMsg")&&($("dataMsg").textContent="Couldn't export here. Try a different browser.");}
};

/* Word list: German, English, example, folder, tab-separated. Opens in a
   spreadsheet, and re-imports through Add words as-is (the importer reads the
   fourth column as the folder). Progress isn't included; that's what the
   backup is for. Tabs, not semicolons, because sentences can contain ";". */
window.exportWordList=()=>{
  try{
    const clean=v=>String(v||"").replace(/[\t\r\n]+/g," ").trim();
    const rows=state.cards.map(c=>[full(c),c.trans,c.ex,c.folder||""].map(clean).join("\t"));
    download(`vokabelspiel-words-${stamp()}.tsv`,rows.join("\n")+"\n","text/tab-separated-values");
    $("dataMsg")&&($("dataMsg").textContent="Word list saved to your downloads.");
  }catch(e){$("dataMsg")&&($("dataMsg").textContent="Couldn't export here. Try a different browser.");}
};

/* ---------- onboarding ---------- */
const TOUR=[
 {h:"Willkommen",
  p:"This is a vocabulary trainer built around games rather than flashcards. It comes with 20 A1 words in a folder called Example so you can try everything straight away.",
  art:null},
 {h:"Words move through stages",
  p:"The bar across the top of the home screen shows where your words sit: from Neu, through recognising them, knowing their article, producing them yourself, and using them in a sentence. A word moves up by earning credit in that particular skill.",
  art:null},
 {h:"Each game trains one skill",
  p:"Games are grouped on the home screen by the stage they're built for, and the one marked next is whichever suits the largest pile of words you have right now. Playing the recommended one is always a reasonable choice.",
  art:null},
 {h:"Guessing doesn't count for much",
  p:"A correct answer is worth what it actually proves, so a one-in-two guess moves a word far less than typing it from nothing. New words come back in minutes, not days, until they're genuinely stuck.",
  art:null},
 {h:"Make it yours",
  p:"Add or import your own words from a file, sort them into folders, suspend anything you're not ready for, and let AI fill in missing articles and example sentences. Everything is saved in this browser.",
  art:null}
];
let tourStep=0;
function onboarding(){
  const s=TOUR[tourStep];
  el.innerHTML=`<div class="tour">
    <div class="tourdots">${TOUR.map((_,i)=>`<span class="${i===tourStep?"on":""}"></span>`).join("")}</div>
    <div class="tourbody">
      <div class="logo" style="font-size:28px;margin-bottom:10px">${esc(s.h)}</div>
      <p>${esc(s.p)}</p>
    </div>
    <button class="go" id="tnext">${tourStep===TOUR.length-1?"Start learning":"Next"}</button>
    ${tourStep<TOUR.length-1?`<button class="go ghost" id="tskip">Skip</button>`:""}
  </div>`;
  $("tnext").onclick=()=>{
    if(tourStep===TOUR.length-1){state.onboarded=true;save();tourStep=0;go("home");}
    else{tourStep++;onboarding();}
  };
  if($("tskip"))$("tskip").onclick=()=>{state.onboarded=true;save();tourStep=0;go("home");};
}

export { render, go };
